import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { authApi, companiesApi } from '../services/api';
import { CLOUDFLARE_STATIC_ASSETS } from '../config/cloudflareAssets';

type AccessStatus = 'checking' | 'allowed' | 'activation-required' | 'denied' | 'unavailable';

export default function FounderGuard({ children }: { children: ReactNode }) {
  const { companyId } = useParams<{ companyId: string }>();
  const { pathname } = useLocation();
  const companyPath = companyId ? `/company/${companyId}` : '';
  const isCompanySettingsRoute =
    pathname.replace(/\/$/, '') === companyPath ||
    pathname === `${companyPath}/admin` ||
    pathname === `${companyPath}/context`;
  const [status, setStatus] = useState<AccessStatus>('checking');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('checking');
    async function check() {
      const [userResult, companiesResult, companyResult] = await Promise.allSettled([
        authApi.getCurrentUser(),
        companiesApi.list(),
        companyId ? companiesApi.get(companyId) : Promise.resolve(null),
      ]);
      if (cancelled) return;
      const account =
        userResult.status === 'fulfilled' && userResult.value.success
          ? userResult.value.data
          : null;
      const companies =
        companiesResult.status === 'fulfilled' && companiesResult.value.success
          ? companiesResult.value.data
          : null;
      const hasAccountAccess =
        account?.superUser ||
        (account?.isFounder &&
          ['active', 'trialing'].includes(account.founderSubscriptionStatus || ''));
      const hasLegacyAccess = companies?.some(
        (company) => !company.isDemo && ['pro', 'enterprise'].includes(company.subscriptionTier)
      );
      const selectedCompany =
        companyResult.status === 'fulfilled' && companyResult.value?.success
          ? companyResult.value.data
          : null;
      const hasCompanyAccess = selectedCompany?.capabilities?.planningAccess;
      const canActivateCompany = selectedCompany?.capabilities?.manageBilling;
      if (
        hasAccountAccess ||
        hasLegacyAccess ||
        hasCompanyAccess ||
        (isCompanySettingsRoute && canActivateCompany)
      )
        setStatus('allowed');
      else if (canActivateCompany) setStatus('activation-required');
      else if (companyId && companyResult.status === 'rejected') setStatus('unavailable');
      else if (account && companies) setStatus('denied');
      else setStatus('unavailable');
    }
    void check();
    return () => {
      cancelled = true;
    };
  }, [attempt, companyId, isCompanySettingsRoute]);

  if (status === 'allowed') return <>{children}</>;
  if (status === 'activation-required')
    return <Navigate to={`${companyPath}/admin?tab=billing`} replace />;
  if (status === 'denied') return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-strategy-50 to-blue-50 p-6">
      <div className="max-w-md text-center">
        <img
          src={CLOUDFLARE_STATIC_ASSETS.LOGO_ICON}
          alt="StratPlan"
          className="h-16 w-16 mx-auto mb-4"
        />
        {status === 'checking' ? (
          <p role="status" className="text-gray-600 font-medium">
            Checking your workspace access…
          </p>
        ) : (
          <>
            <div role="alert">
              <h1 className="text-xl font-semibold text-gray-900">
                Let’s reconnect your workspace.
              </h1>
              <p className="mt-3 text-gray-600">
                We couldn’t verify your access right now. Try again to continue where you left off.
              </p>
            </div>
            <button
              type="button"
              className="mt-5 rounded-lg bg-blue-600 px-5 py-3 font-medium text-white hover:bg-blue-700"
              onClick={() => setAttempt((previous) => previous + 1)}
            >
              Try again
            </button>
            <Link to="/dashboard" className="ml-4 inline-block text-sm text-blue-700 underline">
              All companies
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
